import React, { useEffect, Fragment, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';
import 'lazysizes';
import 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
import './App.css';
import Alert from './components/common/Alert';
import AlertBar from './components/common/AlertBar';
import OrderAlertBar from './components/OrderAlertBar';
import Image from './components/common/Image';
import AppBar from '../../AppBar';
import Header from './components/Header';
import Footer from '../../Footer';
import { observer } from 'mobx-react';
import { useStores } from './stores';
import { detectAlpha2Country, getLocaleByAlpha2 } from '../../utils/country';
import Login from './pages/Login';
import LoginAgent from './pages/LoginAgent';
import Unsubscribe from './pages/Unsubscribe';
import ActivateProfile from './pages/ActivateProfile';
import ResetPassword from './pages/ResetPassword';
import Collection from './pages/Collection';
import Subscription from './pages/Subscription';
import HelpCenter from './pages/HelpCenter';
import PublicHelpCenter from './pages/PublicHelpCenter';
import PublicContactUs from './pages/PublicContactUs';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import AssembleTutorials from './pages/AssembleTutorials';
import HelpCenterPublic from './pages/HelpCenterPublic';
import Progress from './components/common/Progress';
import { Helmet } from 'react-helmet';
import appStyles from '../../config/appStyles.js';
import { Alert as AlertMUI } from '@material-ui/lab';
import Icon from './components/common/Icon';
import { useTranslation } from 'react-i18next';
import ActivateAccount from './pages/ActivateAccount';
import Migrate from './pages/Migrate';
import {
  countryCodes,
  paymentMethods,
  productIdNative,
  sitesId,
} from '../../stores/Constants.js';
import ConsentsDialog from './components/common/ConsentsDialog.js';
import Button from '@material-ui/core/Button/Button';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { whatsAppSupport } from './constants.js';
import { isMobile } from './utils/responsive.js';
import CloseIcon from '@material-ui/icons/Close';
import PageViewTracker from './components/PageViewTracker.js';
import txt from './utils/txt.js';
import storage from './utils/storage.js';
import Withdraw from './pages/Withdraw.js';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import WhatsAppButton from './components/WhatsAppButton.js';
import ReactivationCampaign from './pages/ReactivationCampaign/ReactivationCampaign.jsx';

const useStyles = makeStyles((theme) => appStyles.app(theme));

const MainLayout = observer(({ children }) => {
  const classes = useStyles();
  const { uiStore } = useStores();
  const { t } = useTranslation(['common']);

  useEffect(() => {
    uiStore.getFooterInfo();
    document.body.id =
      process.env.REACT_APP_BRAND === 'PW' ? 'deagostini' : 'fanhome';
    document.body.classList.add(detectAlpha2Country() || 'en-GB');

    document.documentElement.lang =
      getLocaleByAlpha2(detectAlpha2Country()) || 'en-GB';
  }, [uiStore]);

  return (
    <>
      {uiStore.loader && (
        <Progress
          variant="hover"
          className={classes.fullScreenProgerss}></Progress>
      )}
      <div className={classes.stickyStack}>
        {process.env.REACT_APP_MAINTENANCE_MODE === 'true' && (
          <AlertMUI
            classes={{ root: classes.alertCP }}
            icon={<Icon name="alert"></Icon>}
            severity="info">
            {t('common:maintenanceMode')}
          </AlertMUI>
        )}
        <OrderAlertBar />
        <AppBar />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}>
        <Header />
        <main className={classes.main}>{children}</main>
        <Footer />
      </div>
    </>
  );
});

const withRecaptcha = (Component) => (props) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
    scriptProps={{
      async: true,
    }}>
    <Component {...props} />
  </GoogleReCaptchaProvider>
);

const MyArea = observer(() => {
  const classes = useStyles();
  const { userStore, orderStore, collectionStore } = useStores();
  const {
    hasOrders,
    currentIsModelKit,
    currentOrder,
    orders,
    orderTypes,
    isLoadingOrders,
    hasYoutubePlaylist,
  } = orderStore;
  const { showcases } = collectionStore;
  const { user, isLogged } = userStore;
  const { t } = useTranslation(['common']);
  let location = useLocation();
  const history = useHistory();

  const hasSubscriptions =
    orders.length > 0 &&
    orders.some((el) => el.type === orderTypes.subscription);
  const hasBKIs =
    orders.length > 0 && orders.some((el) => el.type === orderTypes.backissue);

  useEffect(() => {
    userStore.restoreSession();
    if (isLogged) {
      orderStore.fetchOrders();
      orderStore.fetchDebts();
    }
  }, [isLogged]);

  useEffect(() => {
    const push = async () => {
      if (window.snakeAnalytics) {
        window.snakeAnalytics.push([
          'event',
          'login',
          {
            user: {
              im_user_id: user.UID,
              im_user_suscriptor: hasSubscriptions
                ? hasBKIs
                  ? 'both'
                  : 'yes'
                : 'no',
              first_name: user.name,
              last_name: user.lastName,
              email: await txt.hashEmail(user.email),
              phone_number: user?.phone,
              home_address_street: user.billingAddress?.addressLine1,
              home_address_city: user.billingAddress?.city,
              home_address_postal_code: user.billingAddress?.postalCode,
              home_address_country: user.billingAddress?.country,
            },
            eventData: {
              method: 'email',
              im_location: location.pathname + location.search,
            },
          },
        ]);
      }
    };

    if (isLogged && !isLoadingOrders) {
      push();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, isLoadingOrders]);

  useEffect(() => {
    if (currentOrder && currentOrder?.id !== null) {
      collectionStore.fetchCollection(currentOrder);
    }
  }, [currentOrder, collectionStore]);

  useEffect(() => {
    if (currentOrder && currentOrder.variantId) {
      orderStore.fetchProductInfo(currentOrder.variantId);
    }
  }, [currentOrder, orderStore]);

  const hasVideo =
    showcases.some((el) => el.video.source) || hasYoutubePlaylist;

  const profileRequiredFields = [
    'name',
    'lastName',
    'email',
    'phone',
    'gender',
    'birthDay',
    'birthYear',
    'birthMonth',
  ];

  const [isProfileAlertVisible, setProfileAlertVisible] = useState(true);

  const hasIncompleteProfile = profileRequiredFields.some(
    (field) => !user?.[field],
  );

  const showCompleteProfileAlert =
    hasIncompleteProfile &&
    isProfileAlertVisible &&
    storage.get('isProfileAlertVisible') !== 'false';

  const isProfilePage = location.pathname === '/myarea/profile';

  return userStore.isLoading ? (
    <Progress className={classes.fullScreenProgerss} />
  ) : userStore.isLogged ? (
    <>
      {orderStore.showCPMessage && orderStore.isLoadingOrders && (
        <AlertMUI
          classes={{ root: classes.alertCP }}
          icon={<Icon name="alert"></Icon>}
          severity="info">
          {t('common:loadingCP')}
        </AlertMUI>
      )}
      {showCompleteProfileAlert && (
        <div
          className={`${classes.alertCompleteProfile} ${
            isProfilePage ? 'centeredProfileAlert' : ''
          }`}>
          <div className={classes.alertCompleteProfileWrapper}>
            <div className="completeProfileMessage">
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <InfoOutlined fontSize="large" />
                <div className="completeProfileText">
                  <div className="completeProfileTitle">
                    {t('common:completeProfileTitle')}
                  </div>
                  <div className="completeProfileSubtitle">
                    {t('common:completeProfileSubtitle')}
                  </div>
                </div>
              </div>
              {!isMobile() && !isProfilePage && (
                <div className="completeProfileButton">
                  <Button
                    className="completeButton"
                    size="small"
                    variant="outlined"
                    onClick={() => history.push('/myarea/profile')}>
                    {t('common:completeProfileButton')}
                  </Button>
                </div>
              )}
            </div>
            <CloseIcon
              className="cursor-pointer"
              onClick={() => {
                setProfileAlertVisible(false);
                storage.store('isProfileAlertVisible', false);
              }}
            />
          </div>

          {isMobile() && !isProfilePage && (
            <div className="completeProfileButton">
              <Button
                className="completeButton"
                size="small"
                variant="outlined"
                onClick={() => history.push('/myarea/profile')}>
                {t('common:completeProfileButton')}
              </Button>
            </div>
          )}
        </div>
      )}
      {!orderStore.isLoadingOrders &&
        detectAlpha2Country() === countryCodes.pl &&
        currentOrder?.payment?.method === paymentMethods.openInvoce &&
        productIdNative.pl.includes(currentOrder?.productIdNative) && (
          <AlertMUI
            classes={{ root: classes.alertCP }}
            icon={<Icon name="alert"></Icon>}
            severity="info">
            {t('common:reminderBankAccount')}
          </AlertMUI>
        )}
      {currentOrder?.productId === 'ANF401000000' &&
        detectAlpha2Country() === countryCodes.us &&
        process.env.REACT_APP_BRAND === 'FH' && (
          <AlertMUI
            classes={{ root: classes.alertCP }}
            icon={<Icon name="alert"></Icon>}
            severity="info">
            <div
              dangerouslySetInnerHTML={{
                __html: t(
                  'components:OrderAlertBar.notifications.discontinuedShipping',
                ),
              }}
            />
          </AlertMUI>
        )}
      {process.env.REACT_APP_MAINTENANCE_MODE_PL === 'true' &&
        detectAlpha2Country() === countryCodes.pl &&
        currentOrder?.payment?.method === paymentMethods.openInvoce && (
          <AlertMUI
            classes={{ root: classes.alertCP }}
            icon={<Icon name="alert"></Icon>}
            severity="info">
            {t('common:restoreBankAccount')}
          </AlertMUI>
        )}

      <MainLayout>
        <PageViewTracker />
        {user.acquisitionOwner !== sitesId.us && <ConsentsDialog />}
        {!orderStore.isLoadingOrders && (
          <>
            {hasOrders ? (
              <Switch>
                <Route
                  exact
                  path="/myarea/subscription"
                  component={Subscription}
                />
                <Route
                  exact
                  path="/myarea/profile"
                  render={() => (
                    <Profile hasIncompleteProfile={hasIncompleteProfile} />
                  )}
                />
                <Route
                  exact
                  path="/myarea/help-center"
                  component={HelpCenter}
                />
                {currentOrder &&
                currentOrder !== orderTypes.backissue &&
                !currentIsModelKit &&
                !hasVideo ? (
                  <Route path="/myarea/collection/" component={Collection} />
                ) : currentOrder !== orderTypes.backissue ? (
                  <Route path="/myarea/collection">
                    <Redirect to="/myarea/video-tutorials" />
                  </Route>
                ) : (
                  <Route path="/myarea/video-tutorials">
                    <Redirect to="/myarea/subscription" />
                  </Route>
                )}
                {currentIsModelKit || hasVideo ? (
                  <Route
                    path="/myarea/video-tutorials"
                    component={AssembleTutorials}
                  />
                ) : currentOrder !== orderTypes.backissue ? (
                  <Route path="/myarea/video-tutorials">
                    <Redirect to="/myarea/collection" />
                  </Route>
                ) : (
                  <Route path="/myarea/video-tutorials">
                    <Redirect to="/myarea/subscription" />
                  </Route>
                )}
                <Route path="/myarea/dashboard" component={Dashboard} />
                <Route exact path="/myarea">
                  <Redirect to="/myarea/subscription" />
                </Route>
              </Switch>
            ) : (
              <Switch>
                <Route exact path="/myarea/profile" component={Profile} />
                <Route
                  exact
                  path="/myarea/help-center"
                  component={HelpCenter}
                />
                <Route path="/myarea/dashboard" component={Dashboard} />
                <Route exact path="/myarea">
                  <Redirect
                    to={`${
                      process.env.REACT_APP_BRAND === 'FH'
                        ? '/myarea/profile'
                        : '/myarea/dashboard'
                    }`}
                  />
                </Route>
              </Switch>
            )}
          </>
        )}
      </MainLayout>
    </>
  ) : (
    <MainLayout>
      <Login />
    </MainLayout>
  );
});

const PublicArea = () => {
  const { uiStore } = useStores();
  const enabledCountries = [
    countryCodes.cz,
    countryCodes.de,
    countryCodes.it,
    countryCodes.pl,
    countryCodes.sk,
    countryCodes.uk,
    countryCodes.at,
    countryCodes.nl,
    countryCodes.gr,
    countryCodes.fr,
    countryCodes.be,
    countryCodes.es,
    countryCodes.hu,
  ];

  return (
    <MainLayout>
      <PageViewTracker />
      <Switch>
        <Route path="/myarea/p/loginagent" component={LoginAgent} />
        <Route path="/myarea/p/unsubscribe" component={Unsubscribe} />
        <Route path="/myarea/p/activate" component={ActivateProfile} />
        <Route path="/myarea/p/migrate" component={Migrate} />
        <Route path="/myarea/p/reset" component={ResetPassword} />
        <Route path="/myarea/p/activateaccount" component={ActivateAccount} />
        <Route path="/myarea/p/help-center" component={PublicHelpCenter} />
        <Route path="/myarea/p/contact-us" component={HelpCenterPublic} />
        <Route
          path="/myarea/p/reactivation-campaign"
          component={ReactivationCampaign}
        />
        {enabledCountries.includes(uiStore.site) && (
          <Route
            path="/myarea/p/withdraw"
            component={withRecaptcha(Withdraw)}
          />
        )}
        <Redirect to="/myarea/" />
      </Switch>
    </MainLayout>
  );
};

const Services = () => (
  <MainLayout>
    <Switch>
      <Route path="/support/help-centre" component={PublicHelpCenter} />
      <Route path="/support/contact-us" component={PublicContactUs} />
    </Switch>
  </MainLayout>
);

const App = observer(() => {
  const classes = useStyles();
  const { userStore, uiStore, addressStore } = useStores();
  const { modalInfo, alertInfo, closeModal } = uiStore;

  useEffect(() => {
    userStore.init();
    addressStore.initGAddress(uiStore.language, uiStore.isoCountry, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return userStore.isReady ? (
    <div className={classes.root}>
      {[
        countryCodes.be,
        countryCodes.fr,
        countryCodes.es,
        countryCodes.pt,
        countryCodes.ca,
      ].includes(detectAlpha2Country()) ? (
        <Helmet>
          <script
            src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
            data-document-language="true"
            type="text/javascript"
            charset="UTF-8"
            data-domain-script={
              process.env[
                `REACT_APP_COOKIEPRO_URL_${detectAlpha2Country().toUpperCase()}`
              ]
            }></script>
          <script type="text/javascript">{`function OptanonWrapper() {}`}</script>
        </Helmet>
      ) : (
        <Helmet>
          <script
            src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
            data-document-language="true"
            type="text/javascript"
            charset="UTF-8"
            data-domain-script={
              detectAlpha2Country() === countryCodes.us
                ? process.env.REACT_APP_COOKIEPRO_URL_US
                : process.env.REACT_APP_COOKIEPRO_URL
            }></script>
          <script type="text/javascript">{`function OptanonWrapper() {}`}</script>
        </Helmet>
      )}

      {Object.keys(whatsAppSupport).includes(detectAlpha2Country()) &&
        whatsAppSupport[detectAlpha2Country()]?.[process.env.REACT_APP_BRAND] &&
        !window.location.href.includes('/myarea/p/contact-us') && (
          <WhatsAppButton
            number={
              whatsAppSupport[detectAlpha2Country()]?.[
                process.env.REACT_APP_BRAND
              ]
            }
          />
        )}

      <Helmet>
        <script
          src={`${process.env.REACT_APP_ZENDESK_CHAT}?brand=${
            process.env.REACT_APP_BRAND === 'PW' ? 'deagostini' : 'fanhome'
          }&country=${detectAlpha2Country()}`}
        />
      </Helmet>

      {/** global alert sticky */}
      <AlertBar id="header-alert-bar" {...alertInfo} />
      <Router basename={`/${detectAlpha2Country()}`}>
        <Switch>
          <Route path="/myarea/p" component={PublicArea} />
          <Route path="/myarea" component={MyArea} />
          <Route path="/support" component={Services} />
        </Switch>
      </Router>
      {/** Global alert dialog */}
      <Alert
        {...modalInfo}
        icon={
          !!modalInfo.icon ? (
            modalInfo.icon
          ) : modalInfo.isError ? (
            <Image name="warning" />
          ) : (
            <Image name="success" />
          )
        }
        onClose={closeModal}
      />
    </div>
  ) : (
    <Progress className={classes.fullScreenProgerss}></Progress>
  );
});

export default App;
